import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography } from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";
import PlaceIcon from "@mui/icons-material/Place";
import GoogleMapReact from "google-map-react";
import Widget from "../Common/Widget/Widget";
import {
  formatDisplayValue,
  getAqiBand,
} from "../../utils/smartRoomStatusRanges";

const GRAYSCALE_STYLES = [
  { elementType: "geometry", stylers: [{ saturation: -100 }, { lightness: 10 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e0e0e0" }],
  },
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
];

const DEFAULT_RING_KM = 15;

/** Build lat/lng ring path for a radius in meters around a center. */
const buildRingPath = (centerLat, centerLng, radiusMeters, steps = 72) => {
  const path = [];
  const latRad = (centerLat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);

  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * 2 * Math.PI;
    path.push({
      lat: centerLat + (radiusMeters * Math.sin(angle)) / metersPerDegLat,
      lng: centerLng + (radiusMeters * Math.cos(angle)) / metersPerDegLng,
    });
  }
  return path;
};

/** Dashed concentric proximity rings around the room location. */
const drawProximityRings = (maps, map, centerLat, centerLng, baseKm) => {
  const overlays = [];
  const radiusBase =
    Number.isFinite(baseKm) && baseKm > 0 ? baseKm : DEFAULT_RING_KM;
  const radiusFactors = [0.38, 0.68, 1.05];

  radiusFactors.forEach((factor, index) => {
    const radiusMeters = radiusBase * factor * 1000;
    const path = buildRingPath(centerLat, centerLng, radiusMeters);

    overlays.push(
      new maps.Circle({
        map,
        center: { lat: centerLat, lng: centerLng },
        radius: radiusMeters,
        strokeOpacity: 0,
        fillColor: "#90A4AE",
        fillOpacity: index === 0 ? 0.06 : index === 1 ? 0.035 : 0.02,
        clickable: false,
        zIndex: 0,
      })
    );

    overlays.push(
      new maps.Polyline({
        map,
        path,
        strokeOpacity: 0,
        clickable: false,
        zIndex: 1,
        icons: [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeOpacity: 0.55,
              strokeColor: "#90A4AE",
              scale: 2.2,
            },
            offset: "0",
            repeat: "12px",
          },
        ],
      })
    );
  });

  return overlays;
};

const AqiMarker = ({ name, aqi, $hover }) => {
  const band = getAqiBand(aqi);
  const showTooltip = Boolean($hover);

  return (
    <Box
      sx={{
        position: "relative",
        transform: "translate(-50%, -50%)",
        cursor: "pointer",
        zIndex: 2,
      }}
    >
      {showTooltip ? (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: "calc(100% + 10px)",
            transform: "translateX(-50%)",
            bgcolor: "#2F3A3F",
            color: "#fff",
            px: 1.25,
            py: 0.75,
            borderRadius: 1.5,
            whiteSpace: "nowrap",
            boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
            minWidth: 140,
            textAlign: "left",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderWidth: 6,
              borderStyle: "solid",
              borderColor: "#2F3A3F transparent transparent transparent",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
              AQI {formatDisplayValue(aqi, 0)}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: band?.color || "#4CAF50",
          border: "2px solid #fff",
          boxShadow: `0 0 0 6px ${band?.color || "#4CAF50"}33, 0 2px 8px rgba(0,0,0,0.25)`,
        }}
      />
    </Box>
  );
};

AqiMarker.propTypes = {
  name: PropTypes.string,
  aqi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  $hover: PropTypes.bool,
  lat: PropTypes.number,
  lng: PropTypes.number,
};

/**
 * Map card for the selected room location (Home Details lat/lon).
 */
const NearestMonitorMapCard = ({
  lat,
  lon,
  roomName,
  roomAqi,
  height = 220,
}) => {
  const mapRef = useRef(null);
  const mapsApiRef = useRef(null);
  const ringsRef = useRef([]);

  const hasCoords =
    Number.isFinite(Number(lat)) && Number.isFinite(Number(lon));

  const room = useMemo(() => {
    if (!hasCoords) return null;
    return {
      name: roomName || "Indoor",
      lat: Number(lat),
      lon: Number(lon),
      aqi: roomAqi,
    };
  }, [hasCoords, lat, lon, roomName, roomAqi]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

  const center = hasCoords
    ? { lat: Number(lat), lng: Number(lon) }
    : { lat: 28.6, lng: 77.2 };

  const clearRings = useCallback(() => {
    ringsRef.current.forEach((overlay) => {
      if (overlay?.setMap) overlay.setMap(null);
    });
    ringsRef.current = [];
  }, []);

  const renderRings = useCallback(() => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;
    if (!map || !maps || !room) return;

    clearRings();
    ringsRef.current = drawProximityRings(
      maps,
      map,
      room.lat,
      room.lon,
      DEFAULT_RING_KM
    );
  }, [room, clearRings]);

  const handleApiLoaded = ({ map, maps }) => {
    if (!map || !maps) return;
    mapRef.current = map;
    mapsApiRef.current = maps;
    map.setOptions({ styles: GRAYSCALE_STYLES });
    renderRings();
  };

  useEffect(() => {
    renderRings();
    return clearRings;
  }, [renderRings, clearRings]);

  const handleLocate = () => {
    const map = mapRef.current;
    if (!map || !room) return;
    map.panTo({ lat: room.lat, lng: room.lon });
    map.setZoom(10);
    renderRings();
  };

  const title = `${roomName || "Room"} Location`;

  return (
    <Widget
      title={title}
      disableHeaderDivider
      action={
        <IconButton
          size="small"
          onClick={handleLocate}
          disabled={!hasCoords || !apiKey}
          sx={{
            bgcolor: "#E3F2FD",
            color: "#0288D1",
            "&:hover": { bgcolor: "#BBDEFB" },
          }}
          aria-label="Recenter on room"
          title="Recenter map"
        >
          <NearMeIcon fontSize="small" />
        </IconButton>
      }
      contentSx={{ pt: 0, pb: 1.25 }}
      sx={{ overflow: "hidden" }}
    >
      <Box
        sx={{ position: "relative", height, borderRadius: 2, overflow: "hidden" }}
      >
        {!hasCoords || !apiKey ? (
          <Box
            sx={{
              height: "100%",
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              p: 2,
            }}
          >
            <PlaceIcon color="action" />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {hasCoords
                ? "Map key unavailable"
                : "Map unavailable — coordinates not found"}
            </Typography>
          </Box>
        ) : (
          <GoogleMapReact
            bootstrapURLKeys={{ key: apiKey, language: "en", region: "IN" }}
            defaultCenter={center}
            center={center}
            defaultZoom={10}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleApiLoaded}
            options={{
              fullscreenControl: false,
              zoomControl: false,
              mapTypeControl: false,
              streetViewControl: false,
              styles: GRAYSCALE_STYLES,
            }}
            hoverDistance={24}
          >
            {room ? (
              <AqiMarker
                lat={room.lat}
                lng={room.lon}
                name={room.name}
                aqi={room.aqi}
              />
            ) : null}
          </GoogleMapReact>
        )}
      </Box>
    </Widget>
  );
};

NearestMonitorMapCard.propTypes = {
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  roomName: PropTypes.string,
  roomAqi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default NearestMonitorMapCard;
